import CommonConstants, { DATA_TYPES } from './CommonConstants';

const { ACCOUNT_LEVEL, ORGANIZATION_LEVEL, CONTACT_LEVEL, PIPELINE_LEVEL } =
  CommonConstants;

const levels = {
  ACCOUNT: 'account',
  ORGANISATION: 'organization',
  ORGANISATION_OPPORTUNITY: 'org_opportunity',
  ORGANISATION_RISK: 'org_risk',
  ACCOUNT_OPPORTUNITY: 'account_opportunity',
  ACCOUNT_RISK: 'account_risk',
  PIPELINE_RISK: 'pip_risk',
  PIPELINE_OPPORTUNITY: 'pip_opportunity',
  CONTACT: 'contact',
};
const ALLOWED_DATA_TYPES = [
  DATA_TYPES.string,
  DATA_TYPES.boolean,
  DATA_TYPES.number,
  DATA_TYPES.date,
  DATA_TYPES.organization,
  DATA_TYPES.account,
  DATA_TYPES.contact,
  DATA_TYPES.user,
  DATA_TYPES.singlePicklist,
  DATA_TYPES.multiPicklist,
  DATA_TYPES.organizationPicklist,
  DATA_TYPES.accountPicklist,
  DATA_TYPES.contactPicklist,
  DATA_TYPES.userPicklist,
  DATA_TYPES.currency,
];
const constants = {
  ALLOWED_DATA_TYPES,
  tabs: {
    ALL: 'ALL',
    PUBLIC: 'PUBLIC',
    PRIVATE: 'PRIVATE',
    PINNED: 'PINNED',
  },
  segmentLevelToLevelMap: {
    [levels.ACCOUNT]: ACCOUNT_LEVEL,
    [levels.ACCOUNT_OPPORTUNITY]: PIPELINE_LEVEL,
    [levels.ACCOUNT_RISK]: PIPELINE_LEVEL,
    [levels.ORGANISATION]: ORGANIZATION_LEVEL,
    [levels.ORGANISATION_OPPORTUNITY]: PIPELINE_LEVEL,
    [levels.ORGANISATION_RISK]: PIPELINE_LEVEL,
    [levels.CONTACT]: CONTACT_LEVEL,
  },
  levels,
  operators: {
    AND: 'AND',
    OR: 'OR',
    AND_WITH: 'AND_WITH',
    OR_WITH: 'OR_WITH',
  },
  comparatorLabelLookup: {
    isSet: 'is set',
    isSetTo: 'includes',
    isNotSet: 'is not set',
    equals: 'is equal to',
    greater: 'is greater than',
    between: 'between',
    lesser: 'is less than',
    greaterEqual: 'is greater than or equal to',
    lesserEqual: 'is lesser than or equal to',
    contains: 'contains',
    in: 'in',
  },

  dateComparatorLabelLookup: {
    isSet: 'is set',
    isNotSet: 'is not set',
    equals: 'on',
    greater: 'after',
    lesser: 'before',
    between: 'between',
    greaterEqual: 'on or after',
    lesserEqual: 'on or before',
    relativeEquals: 'is equal to',
    relativeGreater: 'is greater than',
    relativeLesser: 'is less than',
    daysAgo: { singular: 'day ago', plural: 'days ago' },
    daysFromNow: { singular: 'day from now', plural: 'days from now' },
  },
  comparatorDropdown: {
    string: [
      { id: 'isSet', display: 'is set' },
      { id: 'isNotSet', display: 'is not set' },
      { id: 'equals', display: 'is equal to' },
      { id: 'contains', display: 'contains' },
    ],
    date: {
      sections: [
        {
          name: 'absolute',
          displayTitle: 'Specific dates',
          options: [
            { section: 'absolute', id: 'isSet', display: 'is set' },
            { section: 'absolute', id: 'isNotSet', display: 'is not set' },
            { section: 'absolute', id: 'equals', display: 'on' },
            { section: 'absolute', id: 'greater', display: 'after' },
            { section: 'absolute', id: 'lesser', display: 'before' },
            { section: 'absolute', id: 'between', display: 'between' },
            { section: 'absolute', id: 'greaterEqual', display: 'on or after' },
            { section: 'absolute', id: 'lesserEqual', display: 'on or before' },
          ],
        },
        {
          name: 'relative',
          displayTitle: 'Relative dates',
          options: [
            {
              section: 'relative',
              id: 'relativeEquals',
              display: 'is equal to',
            },
            {
              section: 'relative',
              id: 'relativeGreater',
              display: 'is greater than',
            },
            {
              section: 'relative',
              id: 'relativeLesser',
              display: 'is less than',
            },
          ],
        },
      ],
    },
    number: [
      { id: 'isSet', display: 'is set' },
      { id: 'isNotSet', display: 'is not set' },
      { id: 'equals', display: 'is equal to' },
      { id: 'greater', display: 'is greater than' },
      { id: 'lesser', display: 'is less than' },
      { id: 'greaterEqual', display: 'is greater than or equal to' },
      { id: 'lesserEqual', display: 'is lesser than or equal to' },
    ],
    count: [
      { id: 'equals', display: 'is equal to' },
      { id: 'greater', display: 'is greater than' },
      { id: 'lesser', display: 'is less than' },
      { id: 'greaterEqual', display: 'is greater than or equal to' },
      { id: 'lesserEqual', display: 'is lesser than or equal to' },
    ],
    currency: [
      { id: 'isSet', display: 'is set' },
      { id: 'isNotSet', display: 'is not set' },
      { id: 'equals', display: 'is equal to' },
      { id: 'greater', display: 'is greater than' },
      { id: 'lesser', display: 'is less than' },
      { id: 'greaterEqual', display: 'is greater than or equal to' },
      { id: 'lesserEqual', display: 'is lesser than or equal to' },
    ],
    picklist: [
      { id: 'isSet', display: 'is set' },
      { id: 'isNotSet', display: 'is not set' },
      { id: 'equals', display: 'is equal to' },
      { id: 'in', display: 'includes' },
    ],
    boolean: [
      { id: 'isSet', display: 'is set' },
      { id: 'isNotSet', display: 'is not set' },
      { id: 'equals', display: 'is equal to' },
    ],
    dropdown: [
      { id: 'isNotSet', display: 'is not set' },
      { id: 'isSetTo', display: 'includes' },
    ],
    tags: [
      { id: 'isNotSet', display: 'is not set' },
      { id: 'isSetTo', display: 'includes' },
    ],
    virtualizedTags: [
      { id: 'isNotSet', display: 'is not set' },
      { id: 'isSetTo', display: 'includes' },
    ],
    pipelineDropdown: [
      { id: 'isSet', display: 'is set' },
      { id: 'isNotSet', display: 'is not set' },
      { id: 'isSetTo', display: 'includes' },
      { id: 'contains', display: 'name contains' },
    ],
    default: [
      { id: 'equals', display: 'is equal to' },
      { id: 'isSet', display: 'is set' },
      { id: 'isNotSet', display: 'is set' },
    ],
  },
  defaultValue: null,
  defaultComparatorValue: 'equals',
  isSetComparatorValue: 'isSet',
  isNotSetComparatorValue: 'isNotSet',
  inComparatorValue: 'in',
  betweenComparatorValue: 'between',
};

export default constants;
